import type {
  AddLinePayload,
  Cart,
  CheckoutCreateResponse,
  CheckoutEmbedErrorCode,
  CheckoutEmbedEvent,
  CheckoutEmbedTerminalPayload,
  CheckoutMountMessage,
  CheckoutPayload,
  CheckoutResult,
  CopeCartOptions,
  CreateCartPayload,
  MountedCheckout,
  MountCheckoutCallbacks,
  MountCheckoutOptions,
  PrefilledCheckoutInput,
  PrefilledCheckoutLineInput,
  PrefilledCheckoutResult,
  SdkProduct,
  UpdateBuyerIdentityPayload,
  UpdateLinePayload,
} from './types.js'
import { CopeApiError, CopeCartExpiredError, CopeError, CopeNetworkError } from './types.js'
import type { CartState } from './types.js'

const TERMINAL_STATES: ReadonlySet<CartState> = new Set(['ordered', 'expired', 'abandoned', 'merged'])
const STORAGE_KEY = 'cope_cart'
const REQUEST_TIMEOUT_MS = 8_000
const RETRY_INITIAL_DELAY_MS = 500
const RETRY_MAX_DELAY_MS = 5_000
const CHECKOUT_MESSAGE_SOURCE = 'cope.checkout'
const CHECKOUT_MESSAGE_VERSION = 1
const CHECKOUT_MOUNT_MESSAGE_TYPE = 'copecart.checkout.mount'
const CHECKOUT_MOUNT_MESSAGE_VERSION = 1
const CHECKOUT_MOUNT_RETRY_INTERVAL_MS = 250
const CHECKOUT_MOUNT_MAX_ATTEMPTS = 20
const CHECKOUT_READY_TIMEOUT_MS = 8_000

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'
type MountCleanup = () => void
type RequestOptions = {
  readonly skipAuth?: boolean
  readonly idempotencyKey?: string
}
type PrefilledCheckoutCreateResponse = CheckoutCreateResponse & {
  readonly cart_id: string
  readonly checkout_token?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isCheckoutEmbedErrorCode(value: string): value is CheckoutEmbedErrorCode {
  return (
    value === 'not_found'
    || value === 'load_failed'
    || value === 'embed_not_allowed'
    || value === 'confirm_failed'
  )
}

function isCheckoutEmbedTerminalStatus(value: string): value is CheckoutEmbedTerminalPayload['status'] {
  return (
    value === 'expired'
    || value === 'completed'
    || value === 'unavailable'
    || value === 'cancelled'
    || value === 'processing'
  )
}

function currentWindowOrigin(): string {
  const location = window.location
  if (location.origin) return location.origin

  const host = location.host || [location.hostname, location.port].filter(Boolean).join(':')
  return new URL(`${location.protocol}//${host}`).origin
}

function normalizeCheckoutUrls(absoluteUrl: string): { checkoutUrl: string; embedCheckoutUrl: string } {
  const checkoutUrl = new URL(absoluteUrl)
  const embedCheckoutUrl = new URL(absoluteUrl)
  const embedMatch = checkoutUrl.pathname.match(/^\/checkout\/embed\/([^/]+)$/)
  const checkoutMatch = checkoutUrl.pathname.match(/^\/checkout\/([^/]+)$/)

  if (embedMatch) {
    checkoutUrl.pathname = `/checkout/${embedMatch[1]}`
    embedCheckoutUrl.pathname = `/checkout/embed/${embedMatch[1]}`
    return {
      checkoutUrl: checkoutUrl.toString(),
      embedCheckoutUrl: embedCheckoutUrl.toString(),
    }
  }

  if (checkoutMatch) {
    checkoutUrl.pathname = `/checkout/${checkoutMatch[1]}`
    embedCheckoutUrl.pathname = `/checkout/embed/${checkoutMatch[1]}`
    return {
      checkoutUrl: checkoutUrl.toString(),
      embedCheckoutUrl: embedCheckoutUrl.toString(),
    }
  }

  throw new CopeError('Checkout URL path cannot be mapped to COPE checkout routes')
}

function checkoutEmbedToken(embedCheckoutUrl: string): string {
  const url = new URL(embedCheckoutUrl)
  const match = url.pathname.match(/^\/checkout\/embed\/([^/]+)$/)
  if (!match) {
    throw new CopeError('Checkout embed URL path cannot be mapped to COPE checkout embed route')
  }

  return decodeURIComponent(match[1])
}

function checkoutEmbedOrigin(raw: CheckoutCreateResponse): string | undefined {
  const embed = raw.embed
  if (!isRecord(embed)) return undefined
  if (embed.allowed !== true) return undefined
  if (typeof embed.parent_origin !== 'string') return undefined

  try {
    const origin = new URL(embed.parent_origin).origin
    return origin === embed.parent_origin ? embed.parent_origin : undefined
  } catch {
    return undefined
  }
}

function parseCheckoutEmbedEvent(data: unknown): CheckoutEmbedEvent | null {
  if (!isRecord(data)) return null
  if (data.source !== CHECKOUT_MESSAGE_SOURCE) return null
  if (data.version !== CHECKOUT_MESSAGE_VERSION) return null

  const metadata = {
    ...(typeof data.messageId === 'string' ? { messageId: data.messageId } : {}),
    ...(typeof data.timestamp === 'string' ? { timestamp: data.timestamp } : {}),
  }

  if (data.type === 'ready') {
    return { type: 'ready', ...metadata }
  }

  if (data.type === 'resize') {
    if (!isRecord(data.payload)) return null
    const { height } = data.payload
    if (typeof height !== 'number' || !Number.isFinite(height) || height <= 0) return null
    return {
      type: 'resize',
      payload: { height: Math.ceil(height) },
      ...metadata,
    }
  }

  if (data.type === 'error') {
    if (!isRecord(data.payload)) return null
    const { code, retryable } = data.payload
    if (typeof code !== 'string' || !isCheckoutEmbedErrorCode(code)) return null
    if (typeof retryable !== 'boolean') return null
    return {
      type: 'error',
      payload: { code, retryable },
      ...metadata,
    }
  }

  if (data.type === 'terminal') {
    if (!isRecord(data.payload)) return null
    const { status } = data.payload
    if (typeof status !== 'string' || !isCheckoutEmbedTerminalStatus(status)) return null
    return {
      type: 'terminal',
      payload: { status },
      ...metadata,
    }
  }

  return null
}

/**
 * COPE Cart SDK — browser client for Cart V1 API.
 *
 * Usage:
 * ```ts
 * const cope = new CopeCart({ baseUrl: 'https://app.cope.com/gateway/cart_api' })
 * const cart = await cope.createCart({ currency: 'EUR' })
 * await cope.addLine(cart.id, { product_id: 'uuid-123' })
 * await cope.reprice(cart.id)
 * const checkout = await cope.checkout(cart.id, { success_url: '...', cancel_url: '...', consents: [{ type: 'buyer_tos' }] })
 * cope.redirectToCheckout(checkout)
 * ```
 */
export class CopeCart {
  private readonly publishableKey: string
  private readonly baseUrl: string
  private readonly checkoutBaseUrl: string
  private readonly abortController: AbortController
  private readonly mountCleanups = new Set<MountCleanup>()
  private cachedSecret: string | null = null
  private cachedCartId: string | null = null

  /** The active cart ID from localStorage, or null if no cart exists. */
  get currentCartId(): string | null {
    return this.cachedCartId
  }

  constructor(options: CopeCartOptions) {
    if (!options.publishableKey?.startsWith('cope_pk_')) {
      throw new CopeError('Invalid publishableKey. Must start with "cope_pk_".')
    }

    if (typeof window !== 'undefined' && window.location?.protocol === 'http:' && window.location.hostname !== 'localhost') {
      throw new CopeError('CopeCart SDK requires HTTPS. Refusing to operate on an insecure page.')
    }

    this.publishableKey = options.publishableKey
    this.baseUrl = (options.baseUrl ?? 'https://app.cope.com/gateway/cart_api').replace(/\/+$/, '')
    this.checkoutBaseUrl = options.checkoutBaseUrl
      ? options.checkoutBaseUrl.replace(/\/+$/, '')
      : new URL(this.baseUrl).origin
    this.abortController = new AbortController()

    this.hydrateFromStorage()
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  async createCart(payload: CreateCartPayload): Promise<Cart> {
    return this.createCartRequest(payload, { skipAuth: true })
  }

  async getProduct(productUuid: string): Promise<SdkProduct> {
    return this.get<SdkProduct>(`/products/${productUuid}`, { skipAuth: true })
  }

  async getCart(cartId: string): Promise<Cart> {
    const cart = await this.get<Cart>(`/carts/${cartId}`)
    if (TERMINAL_STATES.has(cart.state)) {
      this.clearStorage()
      throw new CopeCartExpiredError(cart.state)
    }
    return cart
  }

  async addLine(cartId: string, payload: AddLinePayload): Promise<Cart> {
    return this.post<Cart>(`/carts/${cartId}/lines`, payload)
  }

  async updateLine(cartId: string, lineId: string, payload: UpdateLinePayload): Promise<Cart> {
    return this.patch<Cart>(`/carts/${cartId}/lines/${lineId}`, payload)
  }

  async removeLine(cartId: string, lineId: string): Promise<Cart> {
    return this.del<Cart>(`/carts/${cartId}/lines/${lineId}`)
  }

  async setBuyerIdentity(cartId: string, payload: UpdateBuyerIdentityPayload): Promise<Cart> {
    return this.patch<Cart>(`/carts/${cartId}/buyer-identity`, payload)
  }

  async reprice(cartId: string): Promise<Cart> {
    return this.post<Cart>(`/carts/${cartId}/reprice`, {})
  }

  async applyPromoCode(cartId: string, code: string): Promise<Cart> {
    return this.post<Cart>(`/carts/${cartId}/promo-codes`, { code })
  }

  async removePromoCode(cartId: string): Promise<Cart> {
    return this.del<Cart>(`/carts/${cartId}/promo-codes`)
  }

  async checkout(cartId: string, payload: CheckoutPayload): Promise<CheckoutResult> {
    return this.createCheckoutResult(cartId, payload)
  }

  async createPrefilledCheckout(input: PrefilledCheckoutInput): Promise<PrefilledCheckoutResult> {
    const lines = this.normalizePrefilledLines(input)
    const raw = await this.post<PrefilledCheckoutCreateResponse>('/prefilled-checkouts', {
      external_reference: input.externalReference,
      currency: input.currency,
      ...(input.locale != null ? { locale: input.locale } : {}),
      ...(input.attribution ? { attribution: input.attribution } : {}),
      ...(input.intendedPaymentMethod != null ? { intended_payment_method: input.intendedPaymentMethod } : {}),
      ...(input.metadata ? { metadata: input.metadata } : {}),
      lines: lines.map((line) => ({
        product_id: line.productId,
        ...(line.planId !== undefined ? { plan_id: line.planId } : {}),
        ...(line.quantity !== undefined ? { quantity: line.quantity } : {}),
      })),
      buyer_identity: input.buyerIdentity,
      checkout: input.checkout,
    }, {
      skipAuth: true,
      idempotencyKey: this.prefilledIdempotencyKey(input.externalReference),
    })
    const checkout = this.normalizeCheckoutResult(raw)

    return {
      cartId: raw.cart_id,
      cartVersion: raw.cart_version,
      checkoutId: checkout.checkoutId,
      checkoutToken: raw.checkout_token ?? checkoutEmbedToken(checkout.embedCheckoutUrl),
      checkoutUrl: checkout.checkoutUrl,
      embedCheckoutUrl: checkout.embedCheckoutUrl,
      ...(checkout.embedOrigin ? { embedOrigin: checkout.embedOrigin } : {}),
      status: checkout.status,
      expiresAt: checkout.expiresAt,
      totals: checkout.totals,
      ...(input.intendedPaymentMethod != null ? { intendedPaymentMethod: input.intendedPaymentMethod } : {}),
    }
  }

  async cancelCheckout(checkoutId: string): Promise<void> {
    await this.requestVoid('POST', `/checkouts/${checkoutId}/cancel`)
  }

  redirectToCheckout(checkout: CheckoutResult): void {
    if (this.abortController.signal.aborted) return
    if (typeof window !== 'undefined') {
      window.location.href = checkout.checkoutUrl
    }
  }

  mountCheckout(checkout: CheckoutResult, options: MountCheckoutOptions): MountedCheckout
  mountCheckout(target: HTMLElement | string, checkout: CheckoutResult, options?: MountCheckoutCallbacks): MountedCheckout
  mountCheckout(
    checkoutOrTarget: CheckoutResult | HTMLElement | string,
    optionsOrCheckout: MountCheckoutOptions | CheckoutResult,
    maybeOptions: MountCheckoutCallbacks = {},
  ): MountedCheckout {
    if (this.abortController.signal.aborted) {
      throw new CopeError('SDK destroyed')
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new CopeError('mountCheckout requires a browser environment')
    }

    const { checkout, options } = this.normalizeMountCheckoutArgs(
      checkoutOrTarget,
      optionsOrCheckout,
      maybeOptions,
    )
    const target = this.resolveMountTarget(options.target)
    const iframe = document.createElement('iframe')
    const checkoutOrigin = new URL(checkout.embedCheckoutUrl).origin
    const mountMessage: CheckoutMountMessage = {
      type: CHECKOUT_MOUNT_MESSAGE_TYPE,
      version: CHECKOUT_MOUNT_MESSAGE_VERSION,
      token: checkoutEmbedToken(checkout.embedCheckoutUrl),
    }
    const parentOrigin = currentWindowOrigin()
    let destroyed = false
    let ready = false
    let mountAttempts = 0
    let mountRetry: ReturnType<typeof window.setInterval> | undefined
    let readyTimeout: ReturnType<typeof window.setTimeout> | undefined

    if (!checkout.embedOrigin) {
      throw new CopeError('mountCheckout requires checkout.embedOrigin from checkout({ embed_origin })')
    }

    if (checkout.embedOrigin !== parentOrigin) {
      throw new CopeError(`Checkout embed origin ${checkout.embedOrigin} does not match current origin ${parentOrigin}`)
    }

    iframe.src = checkout.embedCheckoutUrl
    iframe.title = options.title ?? 'COPE checkout'
    iframe.setAttribute('allow', 'payment *')
    iframe.referrerPolicy = 'no-referrer'
    iframe.style.border = '0'
    iframe.style.width = '100%'
    iframe.style.minHeight = '720px'
    if (options.className) {
      iframe.className = options.className
    }

    const cleanup = () => {
      if (destroyed) return
      destroyed = true
      if (mountRetry !== undefined) {
        window.clearInterval(mountRetry)
      }
      if (readyTimeout !== undefined) {
        window.clearTimeout(readyTimeout)
      }
      window.removeEventListener('message', handleMessage)
      iframe.remove()
      this.mountCleanups.delete(cleanup)
    }

    const postMountMessage = () => {
      if (destroyed || ready) return
      if (mountAttempts >= CHECKOUT_MOUNT_MAX_ATTEMPTS) {
        if (mountRetry !== undefined) {
          window.clearInterval(mountRetry)
          mountRetry = undefined
        }
        return
      }

      mountAttempts += 1
      iframe.contentWindow?.postMessage(mountMessage, checkoutOrigin)
    }

    const handleMessage = (event: MessageEvent<unknown>) => {
      if (destroyed) return
      if (event.origin !== checkoutOrigin) return
      if (event.source !== iframe.contentWindow) return

      const message = parseCheckoutEmbedEvent(event.data)
      if (!message) return

      options.onMessage?.(message)
      if (message.type === 'ready') {
        ready = true
        if (mountRetry !== undefined) {
          window.clearInterval(mountRetry)
          mountRetry = undefined
        }
        if (readyTimeout !== undefined) {
          window.clearTimeout(readyTimeout)
          readyTimeout = undefined
        }
        options.onReady?.()
      } else if (message.type === 'resize') {
        iframe.style.height = `${message.payload.height}px`
        options.onResize?.(message.payload)
      } else if (message.type === 'error') {
        options.onError?.(message.payload)
      } else if (message.type === 'terminal') {
        options.onTerminal?.(message.payload)
        if (message.payload.status === 'completed') {
          options.onSuccess?.()
        } else if (message.payload.status === 'cancelled') {
          options.onCancel?.()
        }
      }
    }

    window.addEventListener('message', handleMessage)
    this.mountCleanups.add(cleanup)

    try {
      target.appendChild(iframe)
      postMountMessage()
      mountRetry = window.setInterval(postMountMessage, CHECKOUT_MOUNT_RETRY_INTERVAL_MS)
      readyTimeout = window.setTimeout(() => {
        if (destroyed || ready) return
        if (options.fallback === 'redirect') {
          options.onFallbackRedirect?.()
          window.location.href = checkout.checkoutUrl
          return
        }

        options.onError?.({ code: 'load_failed', retryable: true })
      }, options.readyTimeoutMs ?? CHECKOUT_READY_TIMEOUT_MS)
    } catch (error) {
      cleanup()
      throw error
    }

    return { iframe, destroy: cleanup }
  }

  destroy(): void {
    for (const cleanup of Array.from(this.mountCleanups)) {
      cleanup()
    }
    this.abortController.abort()
    this.clearStorage()
  }

  // ---------------------------------------------------------------------------
  // HTTP methods
  // ---------------------------------------------------------------------------

  private async createCartRequest(payload: CreateCartPayload, opts?: RequestOptions): Promise<Cart> {
    const cart = await this.post<Cart>('/carts', payload, opts)
    if (cart.cart_secret) {
      this.cachedCartId = cart.id
      this.cachedSecret = cart.cart_secret
      this.persistToStorage()
    }
    return cart
  }

  private async createCheckoutResult(cartId: string, payload: CheckoutPayload, opts?: RequestOptions): Promise<CheckoutResult> {
    const raw = await this.post<CheckoutCreateResponse>(`/carts/${cartId}/checkout`, payload, opts)
    return this.normalizeCheckoutResult(raw)
  }

  private normalizeCheckoutResult(raw: CheckoutCreateResponse): CheckoutResult {
    const absoluteUrl = raw.checkout_url.startsWith('http')
      ? raw.checkout_url
      : `${this.checkoutBaseUrl}${raw.checkout_url}`

    // Validate redirect URL belongs to expected origin
    const allowedOrigin = new URL(this.checkoutBaseUrl).origin
    const redirectOrigin = new URL(absoluteUrl).origin
    if (redirectOrigin !== allowedOrigin) {
      throw new CopeError(`Checkout URL origin ${redirectOrigin} does not match expected ${allowedOrigin}`)
    }

    const urls = normalizeCheckoutUrls(absoluteUrl)

    return {
      checkoutId: raw.checkout_id,
      checkoutUrl: urls.checkoutUrl,
      embedCheckoutUrl: urls.embedCheckoutUrl,
      embedOrigin: checkoutEmbedOrigin(raw),
      status: raw.status,
      expiresAt: raw.expires_at,
      totals: raw.totals,
    }
  }

  private async requestVoid(method: HttpMethod, path: string, body?: unknown): Promise<void> {
    await this.request<unknown>(method, path, body)
  }

  private async get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, opts)
  }

  private async post<T>(path: string, body: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, opts)
  }

  private async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body)
  }

  private async del<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path)
  }

  private async request<T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    opts?: RequestOptions,
  ): Promise<T> {
    if (this.abortController.signal.aborted) {
      throw new CopeError('SDK destroyed')
    }

    const url = `${this.baseUrl}/api/cart/v1${path}`
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Cope-Key': this.publishableKey,
    }

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    if (!opts?.skipAuth && this.cachedSecret) {
      headers['Authorization'] = `CartSecret ${this.cachedSecret}`
    }

    // Idempotency key for POST — same key across retries
    const idempotencyKey = method === 'POST' ? (opts?.idempotencyKey ?? crypto.randomUUID()) : undefined
    if (idempotencyKey) {
      headers['Idempotency-Key'] = idempotencyKey
    }

    const maxAttempts = (method === 'GET' || (method === 'POST' && this.isRetryablePath(path))) ? 2 : 1
    let lastError: unknown

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        const delay = this.calculateRetryDelay(attempt - 1)
        await this.sleep(delay)
      }

      const timeoutController = new AbortController()
      const timeout = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS)
      const signal = AbortSignal.any([this.abortController.signal, timeoutController.signal])

      try {
        const response = await fetch(url, {
          method,
          headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
          signal,
        })

        if (response.ok) {
          if (response.status === 204) return undefined as T
          return (await response.json()) as T
        }

        // Parse error body
        const errorBody = await response.json().catch(() => ({ errors: [{ code: 'unknown', message: response.statusText }] }))
        const errors = errorBody?.errors ?? [{ code: 'unknown', message: response.statusText }]
        const requestId = response.headers.get('X-Request-Id')

        // Retry on 5xx / 429
        if (this.shouldRetry(response.status) && attempt < maxAttempts - 1) {
          lastError = new CopeApiError(response.status, errors, requestId)
          continue
        }

        throw new CopeApiError(response.status, errors, requestId)
      } catch (error) {
        if (error instanceof CopeApiError) throw error
        if (error instanceof CopeCartExpiredError) throw error

        // AbortError from SDK destroy
        if (this.abortController.signal.aborted) {
          throw new CopeError('SDK destroyed')
        }

        // Network or timeout error — retry if possible
        if (attempt < maxAttempts - 1) {
          lastError = error
          continue
        }

        const msg = error instanceof Error ? error.message : 'Network error'
        throw new CopeNetworkError(msg)
      } finally {
        clearTimeout(timeout)
      }
    }

    // Should not reach here, but just in case
    throw lastError instanceof Error ? lastError : new CopeNetworkError('Request failed')
  }

  private shouldRetry(status: number): boolean {
    return status === 429 || status >= 500
  }

  private isRetryablePath(path: string): boolean {
    return !path.includes('/checkout')
  }

  private calculateRetryDelay(attempt: number): number {
    const exponential = RETRY_INITIAL_DELAY_MS * Math.pow(2, attempt)
    const capped = Math.min(exponential, RETRY_MAX_DELAY_MS)
    return capped * (0.5 + Math.random() * 0.5)
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private normalizePrefilledLines(input: PrefilledCheckoutInput): readonly PrefilledCheckoutLineInput[] {
    if (!isRecord(input)) {
      throw new CopeError('createPrefilledCheckout requires an input object')
    }

    if (!input.externalReference) {
      throw new CopeError('createPrefilledCheckout requires externalReference for backend idempotency')
    }

    if (input.line != null && input.lines != null) {
      throw new CopeError('Use either line or lines for createPrefilledCheckout, not both')
    }

    if (input.line != null) {
      return [input.line]
    }

    if (input.lines == null || input.lines.length === 0) {
      throw new CopeError('createPrefilledCheckout requires at least one line')
    }

    return input.lines
  }

  private prefilledIdempotencyKey(externalReference: string): string {
    const safeReference = externalReference.replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 120)
    // Keep the `:cart` suffix stable; callers and backend idempotency records may already use it.
    return `prefilled:${safeReference}:cart`
  }

  private resolveMountTarget(target: HTMLElement | string): HTMLElement {
    if (typeof target !== 'string') return target

    const element = document.querySelector(target)
    if (element instanceof HTMLElement) return element

    throw new CopeError(`mountCheckout target "${target}" was not found`)
  }

  private normalizeMountCheckoutArgs(
    checkoutOrTarget: CheckoutResult | HTMLElement | string,
    optionsOrCheckout: MountCheckoutOptions | CheckoutResult,
    maybeOptions: MountCheckoutCallbacks,
  ): { checkout: CheckoutResult; options: MountCheckoutOptions } {
    if (this.isCheckoutResult(checkoutOrTarget)) {
      return {
        checkout: checkoutOrTarget,
        options: optionsOrCheckout as MountCheckoutOptions,
      }
    }

    if (!this.isCheckoutResult(optionsOrCheckout)) {
      throw new CopeError('mountCheckout requires a CheckoutResult')
    }

    return {
      checkout: optionsOrCheckout,
      options: { ...maybeOptions, target: checkoutOrTarget },
    }
  }

  private isCheckoutResult(value: unknown): value is CheckoutResult {
    if (!isRecord(value)) return false
    return (
      typeof value.checkoutUrl === 'string'
      && typeof value.embedCheckoutUrl === 'string'
      && typeof value.checkoutId === 'string'
    )
  }

  // ---------------------------------------------------------------------------
  // localStorage persistence
  // ---------------------------------------------------------------------------

  private hydrateFromStorage(): void {
    if (typeof localStorage === 'undefined') return
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as { cartId?: string; secret?: string }
        this.cachedCartId = parsed.cartId ?? null
        this.cachedSecret = parsed.secret ?? null
      }
    } catch {
      // Corrupted or unavailable storage — ignore
    }
  }

  private persistToStorage(): void {
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ cartId: this.cachedCartId, secret: this.cachedSecret }),
      )
    } catch {
      // Storage full or unavailable — SDK still works via in-memory cache
    }
  }

  private clearStorage(): void {
    this.cachedCartId = null
    this.cachedSecret = null
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Ignore
    }
  }
}
